/**
 * MONSTER TOWN — WEARABLES (hats + outfits). MONSTER CREW (src/monster/**).
 *
 * DATA, not engine. A new hat is one entry in `HATS` — nothing else in the game
 * changes. The economy crew owns *selling* these; this file owns *wearing* them.
 *
 * Every builder returns a Group whose ORIGIN is the attach point:
 *   - hats     -> the top of the head, +Z is the face
 *   - outfits  -> the centre of the torso, +Z is the front
 * `s` is the wearer's head width in world units, so one hat fits every shape.
 *
 * BUDGET: a hat is at most ~4 meshes and uses only cached kit geometry.
 */

import type * as THREE from 'three'
import type { PaletteName } from '../core/palette'
import { ball, box, cone, cylinder, group } from '../render/kit'

export interface WearableDef {
  readonly id: string
  /** Palette key used for the creator/shop swatch. */
  readonly swatch: PaletteName
  readonly build: (s: number) => THREE.Group
}

// ---------------------------------------------------------------------------
// hats
// ---------------------------------------------------------------------------

const HAT_LIST: readonly WearableDef[] = [
  {
    id: 'cap',
    swatch: 'roofTeal',
    build: (s) => {
      const g = group('hat_cap')
      const dome = ball(0.5, { color: 'roofTeal', seg: 10 })
      dome.scale.set(s * 0.95, s * 0.5, s * 0.95)
      g.add(dome)
      const brim = box(1, 1, 1, { color: 'roofTeal' })
      brim.scale.set(s * 0.7, s * 0.08, s * 0.62)
      brim.position.set(0, s * 0.04, s * 0.52)
      g.add(brim)
      const nub = ball(0.5, { color: 'flowerYellow', seg: 8 })
      nub.scale.setScalar(s * 0.18)
      nub.position.y = s * 0.24
      g.add(nub)
      return g
    },
  },
  {
    id: 'party',
    swatch: 'flowerPink',
    build: (s) => {
      const g = group('hat_party')
      g.add(cone(s * 0.42, s * 0.85, { color: 'flowerPink', seg: 10 }))
      const pom = ball(0.5, { color: 'flowerYellow', seg: 8 })
      pom.scale.setScalar(s * 0.22)
      pom.position.y = s * 0.92
      g.add(pom)
      return g
    },
  },
  {
    id: 'crown',
    swatch: 'uiCoin',
    build: (s) => {
      const g = group('hat_crown')
      const band = cylinder(s * 0.44, s * 0.22, { color: 'uiCoin', seg: 10 })
      g.add(band)
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2
        const spike = cone(s * 0.1, s * 0.26, { color: 'uiCoin', seg: 6 })
        spike.position.set(Math.sin(a) * s * 0.34, s * 0.22, Math.cos(a) * s * 0.34)
        g.add(spike)
      }
      return g
    },
  },
  {
    id: 'bow',
    swatch: 'flowerRed',
    build: (s) => {
      const g = group('hat_bow')
      const mk = (x: number): THREE.Mesh => {
        const m = ball(0.5, { color: 'flowerRed', seg: 8 })
        m.scale.set(s * 0.34, s * 0.26, s * 0.2)
        m.position.set(x, s * 0.08, 0)
        return m
      }
      g.add(mk(-s * 0.22), mk(s * 0.22))
      const knot = ball(0.5, { color: 'flowerPink', seg: 8 })
      knot.scale.setScalar(s * 0.16)
      knot.position.y = s * 0.08
      g.add(knot)
      return g
    },
  },
  {
    id: 'chef',
    swatch: 'flowerWhite',
    build: (s) => {
      const g = group('hat_chef')
      const band = cylinder(s * 0.42, s * 0.18, { color: 'flowerWhite', seg: 10 })
      g.add(band)
      const puff = ball(0.5, { color: 'flowerWhite', seg: 10 })
      puff.scale.set(s * 0.92, s * 0.62, s * 0.92)
      puff.position.y = s * 0.46
      g.add(puff)
      return g
    },
  },
  {
    id: 'flower',
    swatch: 'flowerPurple',
    build: (s) => {
      const g = group('hat_flower')
      const stem = cylinder(s * 0.05, s * 0.3, { color: 'leaf', seg: 6 })
      stem.position.set(s * 0.22, 0, 0)
      g.add(stem)
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2
        const petal = ball(0.5, { color: 'flowerPurple', seg: 8 })
        petal.scale.setScalar(s * 0.17)
        petal.position.set(s * 0.22 + Math.sin(a) * s * 0.15, s * 0.34, Math.cos(a) * s * 0.15)
        g.add(petal)
      }
      const eye = ball(0.5, { color: 'flowerYellow', seg: 8 })
      eye.scale.setScalar(s * 0.14)
      eye.position.set(s * 0.22, s * 0.36, 0)
      g.add(eye)
      return g
    },
  },
  {
    id: 'beanie',
    swatch: 'wallLilac',
    build: (s) => {
      const g = group('hat_beanie')
      const dome = ball(0.5, { color: 'wallLilac', seg: 10 })
      dome.scale.set(s * 0.98, s * 0.6, s * 0.98)
      g.add(dome)
      const cuff = cylinder(s * 0.5, s * 0.16, { color: 'flowerWhite', seg: 10 })
      cuff.position.y = -s * 0.04
      g.add(cuff)
      return g
    },
  },
  {
    id: 'sunhat',
    swatch: 'sand',
    build: (s) => {
      const g = group('hat_sunhat')
      const brim = cylinder(s * 0.78, s * 0.07, { color: 'sand', seg: 12 })
      g.add(brim)
      const dome = ball(0.5, { color: 'sand', seg: 10 })
      dome.scale.set(s * 0.72, s * 0.5, s * 0.72)
      dome.position.y = s * 0.14
      g.add(dome)
      const band = cylinder(s * 0.38, s * 0.1, { color: 'flowerRed', seg: 10 })
      band.position.y = s * 0.06
      g.add(band)
      return g
    },
  },
]

// ---------------------------------------------------------------------------
// outfits
// ---------------------------------------------------------------------------

const OUTFIT_LIST: readonly WearableDef[] = [
  {
    id: 'scarf',
    swatch: 'fabric',
    build: (s) => {
      const g = group('fit_scarf')
      const ring = cylinder(s * 0.52, s * 0.2, { color: 'fabric', seg: 10 })
      g.add(ring)
      const tail = box(1, 1, 1, { color: 'fabric' })
      tail.scale.set(s * 0.22, s * 0.6, s * 0.12)
      tail.position.set(s * 0.24, -s * 0.34, s * 0.34)
      g.add(tail)
      return g
    },
  },
  {
    id: 'apron',
    swatch: 'flowerWhite',
    build: (s) => {
      const g = group('fit_apron')
      const bib = box(1, 1, 1, { color: 'flowerWhite' })
      bib.scale.set(s * 0.66, s * 0.86, s * 0.1)
      bib.position.set(0, -s * 0.2, s * 0.44)
      g.add(bib)
      const pocket = box(1, 1, 1, { color: 'uiPanelAlt' })
      pocket.scale.set(s * 0.4, s * 0.22, s * 0.06)
      pocket.position.set(0, -s * 0.38, s * 0.5)
      g.add(pocket)
      return g
    },
  },
  {
    id: 'cape',
    swatch: 'roofPurple',
    build: (s) => {
      const g = group('fit_cape')
      const cloth = box(1, 1, 1, { color: 'roofPurple' })
      cloth.scale.set(s * 1.02, s * 1.1, s * 0.12)
      cloth.position.set(0, -s * 0.28, -s * 0.46)
      g.add(cloth)
      const collar = cylinder(s * 0.5, s * 0.16, { color: 'roofRed', seg: 10 })
      collar.position.y = s * 0.2
      g.add(collar)
      return g
    },
  },
  {
    id: 'backpack',
    swatch: 'roofMustard',
    build: (s) => {
      const g = group('fit_backpack')
      const bag = box(1, 1, 1, { color: 'roofMustard' })
      bag.scale.set(s * 0.7, s * 0.7, s * 0.34)
      bag.position.set(0, -s * 0.16, -s * 0.56)
      g.add(bag)
      const flap = box(1, 1, 1, { color: 'woodDark' })
      flap.scale.set(s * 0.72, s * 0.2, s * 0.36)
      flap.position.set(0, s * 0.14, -s * 0.56)
      g.add(flap)
      return g
    },
  },
  {
    id: 'tutu',
    swatch: 'flowerPink',
    build: (s) => {
      const g = group('fit_tutu')
      const skirt = cone(s * 0.86, s * 0.42, { color: 'flowerPink', seg: 12 })
      skirt.rotation.x = Math.PI
      skirt.position.y = -s * 0.42
      g.add(skirt)
      return g
    },
  },
  {
    id: 'vest',
    swatch: 'wallBlue',
    build: (s) => {
      const g = group('fit_vest')
      const mk = (x: number): THREE.Mesh => {
        const m = box(1, 1, 1, { color: 'wallBlue' })
        m.scale.set(s * 0.3, s * 0.8, s * 0.12)
        m.position.set(x, -s * 0.12, s * 0.44)
        return m
      }
      g.add(mk(-s * 0.28), mk(s * 0.28))
      const back = box(1, 1, 1, { color: 'wallBlue' })
      back.scale.set(s * 0.86, s * 0.8, s * 0.1)
      back.position.set(0, -s * 0.12, -s * 0.44)
      g.add(back)
      return g
    },
  },
]

// ---------------------------------------------------------------------------
// lookup
// ---------------------------------------------------------------------------

const HATS = new Map<string, WearableDef>(HAT_LIST.map((h) => [h.id, h]))
const OUTFITS = new Map<string, WearableDef>(OUTFIT_LIST.map((o) => [o.id, o]))

export const HAT_IDS: readonly string[] = HAT_LIST.map((h) => h.id)
export const OUTFIT_IDS: readonly string[] = OUTFIT_LIST.map((o) => o.id)

export function hatDef(id: string): WearableDef | undefined {
  return HATS.get(id)
}

export function outfitDef(id: string): WearableDef | undefined {
  return OUTFITS.get(id)
}

/** Build a hat, or null when the kid is not wearing one / the id is unknown. */
export function hatArt(id: string | null, headWidth: number): THREE.Group | null {
  if (id === null) return null
  const def = HATS.get(id)
  return def === undefined ? null : def.build(headWidth)
}

export function outfitArt(id: string | null, torsoWidth: number): THREE.Group | null {
  if (id === null) return null
  const def = OUTFITS.get(id)
  return def === undefined ? null : def.build(torsoWidth)
}
